import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Package, PlusCircle, RefreshCw, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';

const SKUManager = () => {
  const [skuList, setSkuList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [skuData, setSkuData] = useState({
    articleCode: '',
    size: '',
    color: '',
    stockCount: 0,
    addInSpin: false
  });

  // 1. function for data fetchk
  const fetchSkus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/skus');
      if (Array.isArray(response.data)) {
        setSkuList(response.data);
      } else if (response.data.data && Array.isArray(response.data.data)) {
        setSkuList(response.data.data);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("Data load nahi ho saka. Backend check karein.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkus();
  }, [fetchSkus]);

  // 2.function for save data
  const handleSkuSubmit = async (e) => {
    e.preventDefault();
    if (!skuData.articleCode) return alert("Article Code likhna zaroori hai!");

    try {
      const response = await api.post('/api/skus/add', skuData);
      if (response.status === 200 || response.status === 201) {
        alert("SKU Successfully Add Ho Gaya!");
        setSkuData({ articleCode: '', size: '', color: '', stockCount: 0, addInSpin: false });
        fetchSkus();
      }
    } catch (err) {
      console.error("Save Error:", err);
      alert("Save karte waqt error aya!");
    }
  };

  return (
    <div className="p-8 w-full bg-slate-200 min-h-screen">
      <div className="max-w-7xl mx-auto">
        
        {/* Header  */}
        <div className="flex justify-between items-center mb-10 bg-white p-6 rounded-2xl shadow-md border-2 border-slate-300">
          <div>
            <h2 className="text-4xl font-black text-slate-900 flex items-center gap-3">
              <Package className="text-blue-700" size={36} /> Inventory Management
            </h2>
            <p className="text-slate-700 font-bold mt-1 uppercase tracking-wider text-xs">System Control Panel</p>
          </div>
          <button 
            onClick={fetchSkus}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl hover:bg-blue-800 transition-all font-black shadow-lg active:scale-95 disabled:opacity-50"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            {loading ? "FETCHING..." : "REFRESH LIST"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT: ADD FORM - High Contrast */}
          <div className="lg:col-span-4">
            <form onSubmit={handleSkuSubmit} className="bg-white p-8 rounded-2xl shadow-2xl border-2 border-slate-300 sticky top-8">
              <h3 className="text-2xl font-black mb-6 text-slate-900 flex items-center gap-2 border-b-4 border-slate-100 pb-4">
                <PlusCircle size={24} className="text-green-600" /> NEW ENTRY
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-black text-slate-800 mb-2 block uppercase">Article Code</label>
                  <input 
                    className="w-full border-2 border-slate-300 p-4 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all placeholder:text-slate-400 font-bold text-slate-900" 
                    placeholder="e.g. ART-9922" 
                    value={skuData.articleCode}
                    onChange={e => setSkuData({...skuData, articleCode: e.target.value})} 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-black text-slate-800 mb-2 block uppercase">Color</label>
                    <input 
                      className="w-full border-2 border-slate-300 p-4 rounded-xl focus:border-blue-600 outline-none font-bold text-slate-900" 
                      placeholder="Color" 
                      value={skuData.color}
                      onChange={e => setSkuData({...skuData, color: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-black text-slate-800 mb-2 block uppercase">Size</label>
                    <input 
                      className="w-full border-2 border-slate-300 p-4 rounded-xl focus:border-blue-600 outline-none font-bold text-slate-900" 
                      placeholder="Size" 
                      value={skuData.size}
                      onChange={e => setSkuData({...skuData, size: e.target.value})} 
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-black text-slate-800 mb-2 block uppercase">Stock Count</label>
                  <input 
                    className="w-full border-2 border-slate-300 p-4 rounded-xl focus:border-blue-600 outline-none font-black text-xl text-blue-700" 
                    type="number" 
                    value={skuData.stockCount}
                    onChange={e => setSkuData({...skuData, stockCount: e.target.value})} 
                  />
                </div>
                
                <label className="flex items-center gap-3 p-4 bg-slate-100 rounded-xl cursor-pointer hover:bg-blue-600 hover:text-white transition-all border-2 border-slate-200 group">
                  <input 
                    type="checkbox" 
                    className="w-6 h-6 accent-blue-700"
                    checked={skuData.addInSpin}
                    onChange={e => setSkuData({...skuData, addInSpin: e.target.checked})} 
                  />
                  <span className="text-sm font-black uppercase">Allow in Spin Wheel</span>
                </label>

                <button 
                  type="submit"
                  className="w-full bg-slate-900 text-white py-5 rounded-xl hover:bg-black transition-all font-black shadow-xl mt-4 border-b-4 border-blue-600 active:border-b-0 uppercase tracking-widest"
                >
                  Save to Database
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT: DATA TABLE - Dark Headers & Bold Text */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-300 overflow-hidden">
              <div className="p-6 bg-slate-900 text-white flex justify-between items-center border-b-4 border-blue-600">
                <h3 className="font-black tracking-widest text-lg uppercase">Inventory Stock List</h3>
                <span className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-black shadow-inner border border-blue-400">TOTAL ITEMS: {skuList.length}</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100 text-slate-900 border-b-2 border-slate-300">
                      <th className="p-5 font-black uppercase text-xs">Article Details</th>
                      <th className="p-5 font-black uppercase text-xs">Specs</th>
                      <th className="p-5 font-black uppercase text-xs">Stock Level</th>
                      <th className="p-5 font-black uppercase text-xs">Status</th>
                      <th className="p-5 font-black uppercase text-xs text-center">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y-2 divide-slate-100">
                    {skuList.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-20 text-center">
                          {error ? (
                            <div className="flex flex-col items-center text-red-600 gap-2">
                              <AlertCircle size={48} />
                              <p className="font-black text-xl uppercase italic">{error}</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center text-slate-400 gap-2">
                              <Package size={60} />
                              <p className="font-black text-2xl uppercase italic tracking-tighter">Database Empty</p>
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      skuList.map((sku) => (
                        <tr key={sku.id} className="hover:bg-yellow-50 transition-colors">
                          <td className="p-5">
                            <span className="font-black text-slate-900 block text-xl tracking-tight">{sku.articleCode}</span>
                            <span className="text-xs font-bold text-blue-600 uppercase">System ID: {sku.id}</span>
                          </td>
                          <td className="p-5">
                            <div className="flex gap-2">
                              <span className="bg-slate-900 text-white px-3 py-1 rounded text-xs font-black uppercase">{sku.size || 'N/A'}</span>
                              <span className="bg-blue-100 text-blue-800 border-2 border-blue-200 px-3 py-1 rounded text-xs font-black uppercase">{sku.color || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="p-5">
                            <span className={`font-black text-2xl ${sku.stockCount > 10 ? 'text-green-700' : 'text-red-600'}`}>
                              {sku.stockCount}
                            </span>
                          </td>
                          <td className="p-5">
                            {sku.addInSpin ? 
                              <span className="text-white flex items-center gap-1.5 font-black text-[10px] bg-green-600 px-3 py-1.5 rounded-md w-fit shadow-sm border-b-2 border-green-800">
                                <CheckCircle2 size={12}/> SPIN ACTIVE
                              </span> : 
                              <span className="text-slate-600 text-[10px] font-black bg-slate-200 px-3 py-1.5 rounded-md w-fit border border-slate-300 uppercase">Disabled</span>
                            }
                          </td>
                          <td className="p-5 text-center">
                            <button className="text-slate-400 hover:text-red-600 p-3 transition-all hover:bg-red-100 rounded-xl hover:shadow-md active:scale-90">
                              <Trash2 size={24} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SKUManager;